import { PartagePlusBadge } from 'app/components/partage-plus-badge';
import { ProfileCity } from 'app/design/icons/profile-city';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { type Profile } from 'app/network/profile';
import { useTranslation } from 'react-i18next';
import { Pressable, View } from 'react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { IconInfoCircle } from '@tabler/icons-react-native';
import { ProfilePresence } from './profile-presence';

export function ProfileIdentity({
  isUnavailable,
  onOpenAmbassador,
  profile,
  showSuperPartageur,
  subscribed,
  trustPoints,
  viewedProfileIsAmbassador,
}: {
  isUnavailable: boolean;
  onOpenAmbassador: () => void;
  profile: Profile;
  showSuperPartageur: boolean;
  subscribed?: boolean | null;
  trustPoints?: number;
  viewedProfileIsAmbassador: boolean;
}) {
  return (
    <Column
      flex={1}
      gap={profile.placeDetails ? 'spacing.general.x1' : 'spacing.general.x2'}
      justifyContent="center"
      style={{ marginStart: profile.placeDetails ? 18 : 12 }}
    >
      <Row alignItems="center" flexWrap="wrap" style={{ columnGap: 6, rowGap: 4 }}>
        <Text
          fontWeight="700"
          numberOfLines={2}
          variant={profile.placeDetails ? 'headerXSmall' : 'bodyXLarge'}
          style={{ flexShrink: 1, ...(!profile.placeDetails ? { fontSize: 20, lineHeight: 22 } : {}) }}
        >
          {profile.firstName} {profile.lastName}
        </Text>
        {subscribed && !profile.placeDetails ? <PartagePlusBadge variant="small" /> : null}
      </Row>
      {profile.additionalName ? (
        <Text numberOfLines={1} variant="bodyBase">
          {profile.additionalName}
        </Text>
      ) : null}
      <ProfileIdentityBadges {...{ showSuperPartageur, viewedProfileIsAmbassador, onOpenAmbassador }} />
      <ProfileIdentityLocation profile={profile} />
      <ProfilePresence
        isUnavailable={isUnavailable}
        lastSeenAt={profile.lastSeenAt}
        trustPoints={profile.placeDetails ? trustPoints : undefined}
      />
    </Column>
  );
}

function ProfileIdentityBadges({
  showSuperPartageur,
  viewedProfileIsAmbassador,
  onOpenAmbassador,
}: {
  showSuperPartageur: boolean;
  viewedProfileIsAmbassador: boolean;
  onOpenAmbassador: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      {showSuperPartageur ? (
        <Text numberOfLines={1} variant="bodyBase" width="100%">
          {`${t('superPartageur', { ns: 'common' })}${viewedProfileIsAmbassador ? ' | ' : ''}`}
        </Text>
      ) : null}
      {viewedProfileIsAmbassador ? (
        <Pressable accessibilityRole="button" onPress={onOpenAmbassador}>
          <Row alignItems="center" gap="spacing.general.x1">
            <Text color="text.brand" fontWeight="700" numberOfLines={1} variant="bodyBase">
              {t('ambassadorLabel', { ns: 'neighbour-profile' })}
            </Text>
            <View>
              <IconInfoCircle strokeWidth={ICON_STROKE_WIDTH} size={24} />
            </View>
          </Row>
        </Pressable>
      ) : null}
    </>
  );
}

function ProfileIdentityLocation({ profile }: { profile: Profile }) {
  const { t } = useTranslation();
  const location = [profile.city?.trim(), profile.postalCode?.trim()].filter(Boolean).join('-');
  return (
    <>
      {!profile.placeDetails ? (
        <Row alignItems="center" gap="spacing.general.x0,5">
          <ProfileCity width={12} height={14} />
          <Text fontWeight="600" numberOfLines={1} variant="bodySmall">
            {location || '-'}
            {profile.distance !== undefined
              ? ` · ${t('trust.distance', { distance: Math.max(1, Math.round(profile.distance)), ns: 'neighbour-profile' })}`
              : ''}
          </Text>
        </Row>
      ) : null}
    </>
  );
}
